import { useCallback, useMemo, useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { DashboardLayoutKey } from '../layouts';
import { DashboardWidgetData } from '../components/CustomChartWidget';

export interface DashboardData {
  id: string;
  name: string;
  layoutKey: DashboardLayoutKey;
  slots: (DashboardWidgetData | null)[];
  orderIndex: number;
}

export const GET_DASHBOARDS = gql`
  query GetDashboards {
    getDashboards {
      id
      name
      layoutKey
      orderIndex
      slots {
        id
        title
        fieldId
        sectionId
        chartType
        aggregation
        layout {
          x
          y
          w
          h
          minW
          minH
        }
        visible
        orderIndex
        isSystem
        systemKey
      }
    }
    getActiveDashboard {
      id
      name
      layoutKey
      orderIndex
      slots {
        id
        title
        fieldId
        sectionId
        chartType
        aggregation
        layout {
          x
          y
          w
          h
          minW
          minH
        }
        visible
        orderIndex
        isSystem
        systemKey
      }
    }
  }
`;

export const CREATE_DASHBOARD = gql`
  mutation CreateDashboard($input: CreateDashboardInput!) {
    createDashboard(input: $input) {
      id
      name
      layoutKey
      orderIndex
      slots {
        id
      }
    }
  }
`;

export const UPDATE_DASHBOARD = gql`
  mutation UpdateDashboard($id: ID!, $input: UpdateDashboardInput!) {
    updateDashboard(id: $id, input: $input) {
      id
      name
      layoutKey
      orderIndex
      slots {
        id
      }
    }
  }
`;

export const DELETE_DASHBOARD = gql`
  mutation DeleteDashboard($id: ID!) {
    deleteDashboard(id: $id)
  }
`;

export const SET_DASHBOARD_SLOT = gql`
  mutation SetDashboardSlot($dashboardId: ID!, $slotIndex: Int!, $widgetId: ID) {
    setDashboardSlot(dashboardId: $dashboardId, slotIndex: $slotIndex, widgetId: $widgetId) {
      id
      layoutKey
      slots {
        id
        title
        fieldId
        sectionId
        chartType
        aggregation
        visible
        orderIndex
        isSystem
        systemKey
      }
    }
  }
`;

export const SET_ACTIVE_DASHBOARD = gql`
  mutation SetActiveDashboard($id: ID!) {
    setActiveDashboard(id: $id) {
      id
    }
  }
`;

const useDashboards = () => {
  const [editMode, setEditMode] = useState(false);

  const { data, loading, error, refetch } = useQuery(GET_DASHBOARDS);

  const dashboards: DashboardData[] = useMemo(() => data?.getDashboards || [], [data]);
  const activeFromServer: DashboardData | null = data?.getActiveDashboard || null;

  const [activeId, setActiveId] = useState<string | null>(null);
  const active = activeId
    ? dashboards.find((d) => d.id === activeId) ?? activeFromServer
    : activeFromServer;

  const [createDashboard] = useMutation(CREATE_DASHBOARD, {
    refetchQueries: [{ query: GET_DASHBOARDS }],
  });
  const [updateDashboard] = useMutation(UPDATE_DASHBOARD, {
    refetchQueries: [{ query: GET_DASHBOARDS }],
  });
  const [deleteDashboard] = useMutation(DELETE_DASHBOARD, {
    refetchQueries: [{ query: GET_DASHBOARDS }],
  });
  const [setDashboardSlot] = useMutation(SET_DASHBOARD_SLOT, {
    refetchQueries: [{ query: GET_DASHBOARDS }],
  });
  const [setActiveDashboard] = useMutation(SET_ACTIVE_DASHBOARD, {
    refetchQueries: [{ query: GET_DASHBOARDS }],
  });

  const handleCreate = useCallback(
    async (name: string, layoutKey: DashboardLayoutKey) => {
      const result = await createDashboard({ variables: { input: { name, layoutKey } } });
      const created = result.data?.createDashboard;
      if (created) {
        setActiveId(created.id);
        await setActiveDashboard({ variables: { id: created.id } });
      }
      return created;
    },
    [createDashboard, setActiveDashboard]
  );

  const handleRename = useCallback(
    async (id: string, name: string) => {
      await updateDashboard({ variables: { id, input: { name } } });
    },
    [updateDashboard]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      await deleteDashboard({ variables: { id } });
      if (activeId === id) {
        setActiveId(null);
        await refetch();
      }
    },
    [deleteDashboard, activeId, refetch]
  );

  const handleChangeLayout = useCallback(
    async (id: string, layoutKey: DashboardLayoutKey) => {
      await updateDashboard({ variables: { id, input: { layoutKey } } });
    },
    [updateDashboard]
  );

  const handleSetSlot = useCallback(
    async (dashboardId: string, slotIndex: number, widgetId: string | null) => {
      await setDashboardSlot({
        variables: { dashboardId, slotIndex, widgetId },
      });
    },
    [setDashboardSlot]
  );

  const handleSwitch = useCallback(
    async (id: string) => {
      setActiveId(id);
      await setActiveDashboard({ variables: { id } });
    },
    [setActiveDashboard]
  );

  return {
    dashboards,
    active,
    activeId: active?.id ?? null,
    editMode,
    setEditMode,
    loading,
    error,
    handleCreate,
    handleRename,
    handleDelete,
    handleChangeLayout,
    handleSetSlot,
    handleSwitch,
  };
};

export default useDashboards;
